import { useState } from "react";

import { useT } from "../../shared/i18n";
import { Button, Card } from "../../shared/ui";
import { ClearDataDialog, type ClearDataApi } from "./ClearDataDialog";
import { DeletionWizard, type DeletionWizardApi } from "./DeletionWizard";
import { ExportLinks, ExportNoticeBanner } from "./ExportLinks";
import { useExports } from "./useExports";

export type DataPrivacyApi = ClearDataApi & DeletionWizardApi;

/** Export, clear and delete: the owner's controls over their own data. */
export function DataPrivacy({
  api,
  onDeleted,
}: {
  api: DataPrivacyApi;
  /** The deletion was scheduled and acknowledged; leave the normal shell. */
  onDeleted: () => void;
}) {
  const t = useT();
  const exports = useExports(api);
  const [dialog, setDialog] = useState<"clear" | "delete" | null>(null);
  return (
    <section aria-label={t("account.title")} className="flex flex-col gap-2">
      <Card title={t("account.title")}>
        <div className="flex flex-col gap-3">
          <h3 className="text-callout font-semibold text-fg">
            {t("account.export.heading")}
          </h3>
          <p className="text-caption text-fg-muted">
            {t("account.export.note")}
          </p>
          {exports.notice && <ExportNoticeBanner notice={exports.notice} />}
          <Button
            variant="secondary"
            disabled={exports.busy}
            className="self-start"
            onClick={() => {
              void exports.request();
            }}
          >
            {t("account.export.button")}
          </Button>
          {exports.items !== null && (
            <>
              <h3 className="text-callout font-semibold text-fg">
                {t("account.links.heading")}
              </h3>
              <ExportLinks
                items={exports.items}
                busy={exports.busy}
                onRevoke={(id) => {
                  void exports.revokeOne(id);
                }}
                onRevokeAll={() => {
                  void exports.revokeAll();
                }}
              />
            </>
          )}
          <div className="flex flex-col items-start gap-2 pt-2">
            <Button
              variant="dangerSoft"
              onClick={() => {
                setDialog("clear");
              }}
            >
              {t("account.clear.button")}
            </Button>
            <Button
              variant="dangerSoft"
              onClick={() => {
                setDialog("delete");
              }}
            >
              {t("account.delete.button")}
            </Button>
          </div>
        </div>
      </Card>
      <ClearDataDialog
        open={dialog === "clear"}
        onOpenChange={(open) => {
          setDialog(open ? "clear" : null);
        }}
        api={api}
      />
      {dialog === "delete" && (
        <DeletionWizard
          open
          onOpenChange={(open) => {
            if (!open) setDialog(null);
          }}
          api={api}
          onDone={onDeleted}
        />
      )}
    </section>
  );
}
